/**
 * @fileoverview Shared audio-capture helpers for the web recorders
 * (instructor lesson recording and student rehearsals).
 */

/**
 * Pick the best MediaRecorder audio format this browser supports.
 *
 * Chrome/Firefox record WebM/Opus. Safari — which is every browser on an
 * iPhone, since iOS browsers are all WebKit — does not support WebM capture
 * and throws NotSupportedError from the MediaRecorder constructor if it's
 * requested; it records MP4/AAC instead. So the format must be negotiated,
 * never hardcoded.
 *
 * Returns "" when no preference can be expressed (no MediaRecorder, or
 * isTypeSupported rejects both): construct MediaRecorder with no mimeType
 * and let the browser choose its native format.
 *
 * @returns {"audio/webm" | "audio/mp4" | ""}
 */
export const pickAudioMimeType = () => {
  if (typeof MediaRecorder === "undefined" || !MediaRecorder.isTypeSupported) return "";
  if (MediaRecorder.isTypeSupported("audio/webm")) return "audio/webm";
  if (MediaRecorder.isTypeSupported("audio/mp4")) return "audio/mp4";
  return "";
};

// Extension → MIME type for uploaded audio files whose File.type is empty
// (common when the OS doesn't recognize the extension). Mirrors the backend's
// services/s3.audio_extension_for mapping.
const UPLOAD_EXTENSION_TYPES = {
  m4a: "audio/mp4",
  mp4: "audio/mp4",
  mp3: "audio/mpeg",
  wav: "audio/wav",
  webm: "audio/webm",
  ogg: "audio/ogg",
  oga: "audio/ogg",
  opus: "audio/ogg",
  flac: "audio/flac",
  aac: "audio/aac",
};

/**
 * Resolve the audio MIME type of a user-picked file, or "" when the file
 * doesn't look like audio (video containers and documents are rejected —
 * lesson uploads are audio-only). Prefers the browser-reported type and
 * falls back to the file extension, since File.type is often empty.
 *
 * @param {File} file
 * @returns {string}
 */
export const resolveAudioUploadType = (file) => {
  const type = (file?.type || "").toLowerCase();
  if (type.startsWith("audio/")) return type;
  if (type) return ""; // a real but non-audio type (video/*, application/*)
  const ext = (file?.name || "").split(".").pop()?.toLowerCase();
  return UPLOAD_EXTENSION_TYPES[ext] || "";
};

/**
 * Read an audio file's duration (seconds) from its metadata by loading it
 * into a detached <audio> element. Rejects when the browser can't decode
 * the file or reports a non-finite duration (some streams carry none) —
 * duration_seconds drives the usage quota and the length caps, so a file
 * we can't measure honestly is a file we don't upload.
 *
 * @param {Blob} file
 * @returns {Promise<number>}
 */
export const readAudioDuration = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const audio = document.createElement("audio");
    audio.preload = "metadata";
    const cleanup = () => {
      URL.revokeObjectURL(url);
      audio.removeAttribute("src");
    };
    audio.onloadedmetadata = () => {
      const seconds = audio.duration;
      cleanup();
      if (Number.isFinite(seconds) && seconds > 0) resolve(seconds);
      else reject(new Error("unreadable audio duration"));
    };
    audio.onerror = () => {
      cleanup();
      reject(new Error("undecodable audio file"));
    };
    audio.src = url;
  });

/**
 * Whether this page is running in an iOS browser (they're all WebKit).
 *
 * iOS suspends a web page — and pauses or kills its microphone capture —
 * whenever the screen locks, the user switches apps, or another Safari tab
 * takes the foreground. No web page can prevent that (there is no
 * background-audio entitlement for websites; only the native app has one),
 * so the recorders use this to warn upfront that recording is
 * foreground-only rather than letting the first truncated lesson teach it.
 *
 * iPadOS ≥13 masquerades as macOS in the user agent; the multi-touch
 * check catches it (no shipping Mac has a touch screen).
 */
export const isIOSBrowser = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/.test(ua)) return true;
  return /Mac/.test(ua) && (navigator.maxTouchPoints || 0) > 1;
};
